// DSL Preprocessor for handling multi-line expressions with curly brackets

/**
 * Preprocesses DSL input to handle curly bracket blocks
 * Converts multi-line expressions within {} to single-line format for backend
 * @param {string} input - Raw DSL input with potential curly brackets
 * @returns {string} - Processed DSL input compatible with backend
 */
export const preprocessDSLInput = (input) => {
  if (!input || typeof input !== 'string') {
    return input;
  }

  let processed = input;
  let braceDepth = 0;
  let currentBlock = '';
  let result = '';
  let i = 0;

  while (i < processed.length) {
    const char = processed[i];

    if (char === '{') {
      if (braceDepth === 0) {
        // Starting a new block
        currentBlock = '';
      } else {
        // Nested braces - keep them
        currentBlock += char;
      }
      braceDepth++;
    } else if (char === '}') {
      braceDepth--;
      if (braceDepth === 0) {
        // End of block - process and add to result
        const normalizedBlock = normalizeWhitespace(currentBlock);
        result += normalizedBlock;
        currentBlock = '';
      } else {
        // Still inside nested braces
        currentBlock += char;
      }
    } else if (braceDepth > 0) {
      // Inside a block
      currentBlock += char;
    } else {
      // Outside any blocks
      result += char;
    }

    i++;
  }

  // Handle unclosed braces
  if (braceDepth > 0) {
    result += currentBlock;
  }

  return result.trim();
};

/**
 * Normalizes whitespace within a curly bracket block
 * @param {string} block - Content inside curly brackets
 * @returns {string} - Normalized single-line expression
 */
const normalizeWhitespace = (block) => {
  return block
    .replace(/\s+/g, ' ')  // Replace multiple whitespace with single space
    .replace(/\s*,\s*/g, ', ')  // Normalize comma spacing
    .replace(/\s*\(\s*/g, '(')  // Remove space after opening parenthesis
    .replace(/\s*\)\s*/g, ')')  // Remove space before closing parenthesis
    .replace(/\s*\|\s*/g, ' | ')  // Normalize pipe spacing for eq() function
    .trim();
};

/**
 * Validates curly bracket syntax
 * @param {string} input - DSL input to validate
 * @returns {object} - Validation result with isValid and error message
 */
export const validateBracketSyntax = (input) => {
  let braceCount = 0;
  let position = 0;

  for (const char of input) {
    if (char === '{') {
      braceCount++;
    } else if (char === '}') {
      braceCount--;
      if (braceCount < 0) {
        return {
          isValid: false,
          error: `Unexpected closing brace at position ${position}`
        };
      }
    }
    position++;
  }

  if (braceCount > 0) {
    return {
      isValid: false,
      error: `${braceCount} unclosed opening brace(s)`
    };
  }

  return { isValid: true, error: null };
};